/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */


const styles = css`
  :host {
    display: block;
  }

  textarea {
    background: var(--background, transparent);
    border: 1px solid var(--input, #e4e4e7);
    border-radius: 8px;
    box-sizing: border-box;
    color: var(--foreground, #09090b);
    font: inherit;
    font-size: 1rem;
    line-height: 1.4;
    min-height: 64px;
    min-width: 0;
    outline: none;
    padding: 8px 10px;
    resize: vertical;
    transition:
      border-color 140ms ease,
      box-shadow 140ms ease,
      opacity 140ms ease;
    width: 100%;
  }

  textarea:focus {
    border-color: var(--ring, #18181b);
    box-shadow: 0 0 0 3px var(--ring-shadow, rgb(24 24 27 / 12%));
  }

  textarea::placeholder {
    color: var(--muted-foreground, #71717a);
  }

  textarea:disabled {
    background: var(--input-disabled, rgb(244 244 245 / 50%));
    cursor: not-allowed;
    opacity: 0.5;
  }

  @media (min-width: 768px) {
    textarea {
      font-size: 0.875rem;
    }
  }
`;

export class AppTextarea extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppTextarea)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["disabled","data-disabled","name","data-name","placeholder","data-placeholder","required","data-required","rows","data-rows","value","data-value"];

  #disabled = false;
  #name = "";
  #placeholder = "";
  #required = false;
  #rows = "3";
  #value = "";
  #disabledSet = false;
  #nameSet = false;
  #placeholderSet = false;
  #requiredSet = false;
  #rowsSet = false;
  #valueSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #attr0;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #area = (() => { const self = this; return { get current() { return self.#root ? self.#root.querySelector('[data-argon-ref="area"]') : null; } }; })();
  #hooksLive = false;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get disabled() {
    return this.#disabled;
  }

  set disabled(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#disabled, next)) return;
    this.#disabled = next;
    this.#disabledSet = true;
    this.#updateBindings('disabled');
  }

  get name() {
    return this.#name;
  }

  set name(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#name, next)) return;
    this.#name = next;
    this.#nameSet = true;
    this.#updateBindings('name');
  }

  get placeholder() {
    return this.#placeholder;
  }

  set placeholder(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#placeholder, next)) return;
    this.#placeholder = next;
    this.#placeholderSet = true;
    this.#updateBindings('placeholder');
  }

  get required() {
    return this.#required;
  }

  set required(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#required, next)) return;
    this.#required = next;
    this.#requiredSet = true;
    this.#updateBindings('required');
  }

  get rows() {
    return this.#rows;
  }

  set rows(value) {
    const raw = value ?? "3";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#rows, next)) return;
    this.#rows = next;
    this.#rowsSet = true;
    this.#updateBindings('rows');
  }

  get value() {
    return this.#value;
  }

  set value(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#value, next)) return;
    this.#value = next;
    this.#valueSet = true;
    this.#updateBindings('value');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-disabled')) this.disabled = this.dataset.disabled;
    if (this.hasAttribute('data-name')) this.name = this.dataset.name;
    if (this.hasAttribute('data-placeholder')) this.placeholder = this.dataset.placeholder;
    if (this.hasAttribute('data-required')) this.required = this.dataset.required;
    if (this.hasAttribute('data-rows')) this.rows = this.dataset.rows;
    if (this.hasAttribute('data-value')) this.value = this.dataset.value;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.disabled = props.disabled;
      this.name = props.name;
      this.placeholder = props.placeholder;
      this.required = props.required;
      this.rows = props.rows;
      this.value = props.value;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#disabledSet) this.disabled = this.dataset.disabled;
    if (!this.#nameSet) this.name = this.dataset.name;
    if (!this.#placeholderSet) this.placeholder = this.dataset.placeholder;
    if (!this.#requiredSet) this.required = this.dataset.required;
    if (!this.#rowsSet) this.rows = this.dataset.rows;
    if (!this.#valueSet) this.value = this.dataset.value;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    this.#effect0();
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    if (this.#effect0Cleanup) { this.#effect0Cleanup(); this.#effect0Cleanup = undefined; }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'disabled':
      case 'data-disabled':
        this.disabled = newValue;
        break;
      case 'name':
      case 'data-name':
        this.name = newValue;
        break;
      case 'placeholder':
      case 'data-placeholder':
        this.placeholder = newValue;
        break;
      case 'required':
      case 'data-required':
        this.required = newValue;
        break;
      case 'rows':
      case 'data-rows':
        this.rows = newValue;
        break;
      case 'value':
      case 'data-value':
        this.value = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><textarea data-argon-ref="area" name="${this.#escAttr(this.#name)}" placeholder="${this.#escAttr(this.#placeholder)}" rows="${this.#escAttr(this.#rows)}" data-argon="1842" data-argon-bind="0"><!--argon-text:0-->${this.#escText(this.#value)}<!--/argon-text:0--></textarea>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  #effect0Cleanup;
  #effect0() {
    if (this.#effect0Cleanup) this.#effect0Cleanup();
    const cleanup = (() => {
    const el = this.#area.current;
    if (!el)
        return;
    el.toggleAttribute("disabled", this.#disabled);
    el.toggleAttribute("required", this.#required);
})();
    this.#effect0Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined || prop === 'value') {
      const __argonNext = `${this.#escText(this.#value)}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'name') && this.#attr0) {
      const __argonNext = `${this.#name}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('name', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'placeholder') && this.#attr0) {
      const __argonNext = `${this.#placeholder}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr0.setAttribute('placeholder', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'rows') && this.#attr0) {
      const __argonNext = `${this.#rows}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr0.setAttribute('rows', __argonNext);
      }
    }
    if (this.#hooksLive && (prop === 'disabled' || prop === 'required')) this.#effect0();
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '1842').forEach(el => this.#listen(el, '1842', 'input', (event: Event) => {
    this.value = (event.target as HTMLTextAreaElement).value;
    this.dispatchEvent(new CustomEvent("value-change", { bubbles: true, composed: true, detail: { value: this.value } }));
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
